// ── Date / time ──────────────────────────────────────────────────────────────

export function isoToday(d) {
  const x = new Date(d);
  return x.getFullYear() + '-' + String(x.getMonth() + 1).padStart(2, '0') + '-' + String(x.getDate()).padStart(2, '0');
}

export function isoMake(y, m, d) {
  return y + '-' + String(m).padStart(2, '0') + '-' + String(d).padStart(2, '0');
}

export function nightCount(a, b) {
  if (!a || !b) return 0;
  return Math.max(0, Math.round((new Date(b + 'T12:00:00') - new Date(a + 'T12:00:00')) / 86400000));
}

export function overlap(a1, a2, b1, b2) {
  return a1 < b2 && b1 < a2;
}

export function fmtS(d) {
  if (!d) return '';
  const x = new Date(d + 'T12:00:00');
  return x.getDate() + '/' + (x.getMonth() + 1);
}

export function fmtF(d) {
  if (!d) return '';
  const x = new Date(d + 'T12:00:00');
  return x.getDate() + '/' + (x.getMonth() + 1) + '/' + x.getFullYear();
}

// ── Phone normalisation ───────────────────────────────────────────────────────

export function cleanPhone(p) {
  let s = (p || '').replace(/[\s\-\(\)\.\+]/g, '');
  if (s.startsWith('00')) s = s.slice(2);
  // Bulgarian: 08xxxxxxxx → 359xxxxxxxx
  if (s.startsWith('0') && s.length >= 9 && s.length <= 10 && !s.startsWith('030')) {
    s = '359' + s.slice(1);
  }
  // Greek: 69xxxxxxxx or 2xxxxxxxxx (10 digits starting with 6 or 2) → 30xxxxxxxxxx
  if ((s.startsWith('6') || s.startsWith('2')) && s.length === 10) {
    s = '30' + s;
  }
  return s;
}

export function formatPhone(p) {
  const c = cleanPhone(p);
  if (c.startsWith('359') && c.length >= 10) {
    return '+' + c.slice(0, 3) + ' ' + c.slice(3, 5) + ' ' + c.slice(5, 8) + ' ' + c.slice(8);
  }
  if (c.startsWith('30') && c.length >= 12) {
    return '+' + c.slice(0, 2) + ' ' + c.slice(2, 4) + ' ' + c.slice(4, 7) + ' ' + c.slice(7);
  }
  return p;
}

// Strip formatting chars and leading zero (except +30 Greece which has no trunk prefix)
export function stripPhone(raw, countryCode) {
  let s = (raw || '').replace(/[\s\-\(\)\.]/g, '');
  if (countryCode !== '+30' && s.startsWith('0')) s = s.slice(1);
  return s;
}

// Returns null (valid), 'letters' (non-digits present), or 'length' (wrong digit count for country)
export function validatePhone(stripped, countryCode) {
  if (!stripped) return null;
  if (!/^\d+$/.test(stripped)) return 'letters';
  const len = stripped.length;
  if (countryCode === '+30')       { if (len !== 10) return 'length'; }
  else if (countryCode === '+359') { if (len !== 9)  return 'length'; }
  else if (countryCode === '+44')  { if (len !== 10) return 'length'; }
  else                             { if (len < 8 || len > 12) return 'length'; }
  return null;
}

// ── Pricing ───────────────────────────────────────────────────────────────────

export function getRate(room, dateStr) {
  const md = dateStr.slice(5); // MM-DD
  for (const s of (room.seasons || [])) {
    if (md >= s.fromDate && md <= s.toDate && s.price > 0) return s.price;
  }
  return room.basePrice;
}

export function refTotal(room, ci, co) {
  if (!ci || !co) return 0;
  let sum = 0;
  const cur = new Date(ci + 'T12:00:00');
  const end = new Date(co + 'T12:00:00');
  while (cur < end) {
    sum += getRate(room, isoToday(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return sum;
}

export function refNightly(room, ci, co) {
  const n = nightCount(ci, co);
  return n > 0 ? Math.round(refTotal(room, ci, co) / n) : room.basePrice;
}

export function getMinStay(room, dateStr) {
  const md = dateStr.slice(5); // "MM-DD"
  const s = (room.seasons || []).find(s => s.fromDate <= md && md <= s.toDate);
  return (s && s.min_stay > 1)
    ? { minStay: s.min_stay, label: s.label }
    : { minStay: 1, label: null };
}

export function bcomCalc(gross, commissionPct = 15, vccFeePct = 1.5) {
  const comm = Math.round(gross * (commissionPct / 100) * 100) / 100;
  const vcc  = Math.round(gross * (vccFeePct  / 100) * 100) / 100;
  return { comm, vcc, net: Math.round((gross - comm - vcc) * 100) / 100 };
}

export function cityTaxCalc(property, booking) {
  const rate    = property?.city_tax_rate || 0;
  const country = property?.country || 'GR';
  if (!rate || country === 'GB') return 0;
  const nights  = nightCount(booking.checkIn, booking.checkOut);
  if (country === 'BG') {
    const childCount = Array.isArray(booking.children) ? booking.children.length : 0;
    const persons = (booking.adults || 1) + childCount;
    return Math.round(rate * persons * nights * 100) / 100;
  }
  return Math.round(rate * nights * 100) / 100;
}

// ── DB mapping ────────────────────────────────────────────────────────────────

export function bookingFromDb(row) {
  return {
    id: row.id,
    roomId: row.room_id,
    guestName: row.guest_name,
    phone: row.phone || '',
    email: row.email || '',
    checkIn: row.check_in,
    checkOut: row.check_out,
    price: String(row.price || 0),
    pricePerNight: String(row.price_per_night || 0),
    paymentStatus: row.payment_status || 'Unpaid',
    depositAmount: row.deposit_amount != null ? String(row.deposit_amount) : '',
    source: row.source || '',
    adults: row.adults || 1,
    children: row.children || [],
    extras: row.extras || [],
    note:    row.note  || '',
    lang:    row.lang  || null,
    status: row.status || 'confirmed',
    preferredContact: row.preferred_contact || null,
    createdAt: row.created_at,
    breakfastIncluded: row.breakfast_included ?? false,
    breakfastPrice: Number(row.breakfast_price) || 0,
    extraBeds: Number(row.extra_beds) || 0,
  };
}

export function bookingToDb(booking, propertyId) {
  return {
    id: booking.id,
    property_id: propertyId,
    room_id: booking.roomId,
    guest_name: booking.guestName,
    phone: booking.phone,
    phone_key: cleanPhone(booking.phone),
    email: booking.email || null,
    check_in: booking.checkIn,
    check_out: booking.checkOut,
    price: Number(booking.price) || 0,
    price_per_night: Number(booking.pricePerNight) || null,
    payment_status: booking.paymentStatus,
    deposit_amount: booking.depositAmount ? Number(booking.depositAmount) : null,
    source: booking.source || null,
    adults: booking.adults || 1,
    children: booking.children || [],
    extras: booking.extras || [],
    note:     booking.note || null,
    lang:     booking.lang || null,
    status: booking.status || 'confirmed',
    preferred_contact: booking.preferredContact || null,
    breakfast_included: !!booking.breakfastIncluded,
    breakfast_price: Number(booking.breakfastPrice) || 0,
    extra_beds: Number(booking.extraBeds) || 0,
  };
}

export function roomFromDb(row) {
  return {
    id: row.id,
    name: row.name,
    type: row.type, // 'room' | 'apartment'
    basePrice: Number(row.base_price),
    capacity: row.capacity,
    maxAdults: row.max_adults ?? null,
    maxChildren: row.max_children ?? null,
    seasons: row.seasons || [],
    sortOrder: row.sort_order || 0,
  };
}

export function roomToDb(room, propertyId) {
  return {
    id: room.id,
    property_id: propertyId,
    name: room.name,
    type: room.type,
    base_price: room.basePrice,
    capacity: room.capacity,
    max_adults: room.maxAdults ?? null,
    max_children: room.maxChildren ?? null,
    seasons: room.seasons || [],
    sort_order: room.sortOrder || 0,
  };
}

// ── Misc ──────────────────────────────────────────────────────────────────────

export function uid() {
  return crypto.randomUUID();
}

export function fmtGuests(b, t) {
  const adults = Number(b.adults) || 1;
  const ch = b.children;
  const childCount = Array.isArray(ch) ? ch.length : (Number(ch) || 0);
  const parts = [adults + ' ' + (adults === 1 ? t.adult : t.adults)];
  if (childCount > 0) parts.push(childCount + ' ' + (childCount === 1 ? t.child : t.children));
  return parts.join(' · ');
}

export function buildMultiBookingLabels(bks) {
  // Group by normalised phone key
  const groups = {};
  for (const b of bks) {
    const key = cleanPhone(b.phone);
    if (!key || key.length < 5) continue;
    if (!groups[key]) groups[key] = [];
    groups[key].push(b);
  }

  const labels = {};
  for (const group of Object.values(groups)) {
    if (group.length < 2) continue;

    // Find connected overlap clusters via BFS — each cluster is labeled independently
    // so two separate multi-room stays by the same guest don't bleed into each other
    const visited = new Set();
    for (const seed of group) {
      if (visited.has(seed.id)) continue;
      const cluster = [];
      const queue = [seed];
      visited.add(seed.id);
      while (queue.length > 0) {
        const cur = queue.shift();
        cluster.push(cur);
        for (const other of group) {
          if (!visited.has(other.id) && overlap(cur.checkIn, cur.checkOut, other.checkIn, other.checkOut)) {
            visited.add(other.id);
            queue.push(other);
          }
        }
      }
      if (cluster.length < 2) continue;
      // Stable sort: earlier check-in first, then earlier createdAt
      cluster.sort((x, y) => {
        if (x.checkIn !== y.checkIn) return x.checkIn < y.checkIn ? -1 : 1;
        return (x.createdAt || '') < (y.createdAt || '') ? -1 : 1;
      });
      cluster.forEach((b, i) => { labels[b.id] = i + 1; });
    }
  }

  return labels;
}

// ── Currency ─────────────────────────────────────────────────────────────────

export function curSym(code) {
  switch ((code || 'EUR').toUpperCase()) {
    case 'EUR': return '€';
    case 'GBP': return '£';
    default:    return code || '€';
  }
}

// ── Bank field validators ─────────────────────────────────────────────────────

export function validateBic(value) {
  if (!value || typeof value !== 'string') return false;
  const s = value.toUpperCase();
  return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(s);
}

export function validateSortCode(value) {
  if (!value || typeof value !== 'string') return false;
  return /^\d{6}$/.test(value);
}

const IBAN_LENGTHS = { GR: 27, BG: 22, GB: 22 };

export function parseIban(value, country) {
  if (!value || typeof value !== 'string') {
    return { ok: false, normalised: '', formatted: '', error: 'empty' };
  }

  const expectedLength = IBAN_LENGTHS[country];
  if (!expectedLength) {
    return { ok: false, normalised: '', formatted: '', error: 'unknown_country' };
  }

  // Strip all whitespace and punctuation, uppercase.
  const normalised = value.replace(/[\s\-\.\_]/g, '').toUpperCase();

  if (!normalised.startsWith(country)) {
    return { ok: false, normalised, formatted: '', error: 'country_mismatch' };
  }

  if (normalised.length !== expectedLength) {
    return { ok: false, normalised, formatted: '', error: 'length' };
  }

  // Mod-97 checksum: move first 4 chars to end, convert letters to digits,
  // interpret as big int, check mod-97 === 1.
  const rearranged = normalised.slice(4) + normalised.slice(0, 4);
  let numeric = '';
  for (const ch of rearranged) {
    const code = ch.charCodeAt(0);
    if (code >= 48 && code <= 57) {
      numeric += ch;
    } else if (code >= 65 && code <= 90) {
      numeric += (code - 55).toString(); // A=10, B=11, ... Z=35
    } else {
      return { ok: false, normalised, formatted: '', error: 'invalid_chars' };
    }
  }

  // Compute mod-97 via chunked reduction (safe for big integers in JS).
  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 7) {
    remainder = Number(String(remainder) + numeric.slice(i, i + 7)) % 97;
  }

  if (remainder !== 1) {
    return { ok: false, normalised, formatted: '', error: 'checksum' };
  }

  // Format: groups of 4, space-separated.
  const formatted = normalised.match(/.{1,4}/g).join(' ');

  return { ok: true, normalised, formatted, error: null };
}

// ── Children policy ───────────────────────────────────────────────────────────

export function getChildBand(age, property) {
  const a = Number(age);
  if (!Number.isFinite(a) || String(age ?? '').trim() === '') return 'unknown';
  const childMax = property?.children_max_age;
  if (childMax != null && a >= childMax) return 'adult';
  const infantMax = property?.infant_max_age;
  if (infantMax != null && a <= infantMax) return 'infant';
  return 'child';
}

export function extraBedCost(roomType, extraBedCount, property, nights) {
  const prices = property?.extra_bed_prices || {};
  const pricePerNight = Number(prices[roomType]) || 0;
  const count = Math.max(0, Math.floor(Number(extraBedCount) || 0));
  const n = (Number.isFinite(nights) && nights > 0) ? nights : 0;
  return { pricePerNight, total: pricePerNight * count * n };
}

// ── Breakfast pricing ─────────────────────────────────────────────────────────

export function breakfastCost(property, party, nights) {
  if (!property || !property.breakfast_enabled) return { eligibleCount: 0, total: 0 };
  if (!Number.isFinite(nights) || nights <= 0) return { eligibleCount: 0, total: 0 };

  const mode = property.breakfast_pricing_mode;
  const price = Number(property.breakfast_price_per_person) || 0;

  if (mode === 'included' || mode === 'none' || !mode) {
    return { eligibleCount: 0, total: 0 };
  }

  if (mode === 'per_room') {
    return { eligibleCount: 1, total: price * nights };
  }

  const adults = Number(party?.adults) || 0;
  const childrenList = Array.isArray(party?.children) ? party.children : [];
  const ageBoundary = property.children_max_age;

  let eligibleChildren = 0;
  if (ageBoundary != null) {
    eligibleChildren = childrenList.reduce((sum, c) => {
      const age = Number(c?.age);
      return Number.isFinite(age) && age >= ageBoundary ? sum + 1 : sum;
    }, 0);
  }

  const eligibleCount = adults + eligibleChildren;
  return { eligibleCount, total: price * eligibleCount * nights };
}
